/**
 * Shared queryOptions for the two live-layer caches (design spec §7).
 *
 * `eventsLiveRailKey()` and `eventsLiveRecapKey()` each have TWO producers — the
 * Home leaves (`useLiveEventsRail` / `useLiveRecaps`) and the space-page selector
 * (`useSpaceLiveEvent`) — which must stay byte-identical writers of the same
 * cache entry (same queryFn, page size, staleTime) so React Query dedupes them
 * into one request app-wide. Hand-duplicated options invite silent drift (one
 * page size changes, the other doesn't, and the shared entry flip-flops), so
 * this module is the single definition; consumers spread these and add only
 * their own `enabled` gate.
 */
import { queryOptions } from '@tanstack/react-query';
import { getMyCalendars, getMyRecaps } from '@api-client/api-client';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { eventsLiveRailKey, eventsLiveRecapKey } from '@lib/cache/events';

/**
 * The rail's fetch depth (Live Layer v2; WIDENED 3 → 10 by Dexter's review of
 * PR #1040). The rail SURFACES only a few pills — the display caps live at the
 * render layer (`capRailForDisplay`) — but it must SEE more than it shows,
 * because the client re-ranks what it receives.
 *
 * A THRESHOLD, NOT A GUARANTEE (PR #1044 review — this docblock used to read as
 * though the widening resolved the starvation case; it only moves it up a
 * notch). `my-calendars` is `pov=upcoming`, ordered strictly start-ASC
 * server-side, so live events (start already past) always occupy the FRONT of
 * page 1, and there is no backfill: this page size is a hard depth limit.
 *
 * Below 10 concurrently-live joined-space events the widening does its job — the
 * 3-live case that motivated it now reaches the client. AT OR ABOVE 10 the same
 * pathology returns in full: a member whose joined spaces have ten or more rooms
 * live right now gets exactly those ten rows, and their own event starting in 20
 * minutes is not in the payload at all. The Home rail then renders ten rooms they
 * are not attending and zero pills for the event they said they would attend —
 * not a buried reminder, NO reminder, and no client ordering or cap can recover
 * a row that was never fetched.
 *
 * Closing it for real needs a fetch-shape change, deliberately NOT done here:
 * either a second targeted request for the member's own upcoming RSVPs merged
 * into the rail, or a server-side `pov` that reserves page capacity for
 * `is_going` rows. Until one of those lands, this is a known depth limit rather
 * than a solved problem.
 *
 * Coverage this depth does and does NOT reach: the space-page selector shares
 * this same cache (`useSpaceLiveEvent`) and backfills the SPACE PAGE with its
 * own space-scoped probe, so a joined space's own live surface never starves. It
 * does NOT backfill the Home carousel — `useLiveEventsRail().liveSpaceIds`
 * derives from these entries alone, so `SpacesCarousel` rings (and the Home
 * pill) cover at most 10 concurrently-live joined spaces; the earliest-started
 * win and a member live in 11+ joined spaces loses the ring for the 11th+. The
 * edge gap now starts at 10 (it was 3 here, and 12 before v2); widening the
 * carousel's own coverage is still a deferred product call, not closed here.
 */
const LIVE_RAIL_PER_PAGE = 10;

/**
 * The recap keeps its own, deeper page: `useLiveRecaps` / the space selector
 * must find the most-recently-ended GOING event (per space), which a shallower
 * page could truncate out. Independent of the rail's size above on purpose —
 * recap behavior is unchanged in v2 and by the PR #1040 review's widening.
 */
const LIVE_RECAP_PER_PAGE = 12;

/**
 * Rail source: `GET /events/my-calendars` — the upcoming feed across the user's
 * joined spaces (`pov=upcoming` server-side). Partitioned client-side by each
 * consumer into the always-on rail (upcoming/soon/live entries), ring sets, and
 * the space slot.
 */
export const liveRailQueryOptions = () =>
  queryOptions({
    queryKey: eventsLiveRailKey(),
    queryFn: async () => {
      const response = await getMyCalendars({ page: 1, per_page: LIVE_RAIL_PER_PAGE });
      return response.data.data;
    },
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
  });

/**
 * Recap source: `GET /events/my-recaps` — the user's GOING events whose
 * `end_date` fell within the server's recap window (`RECAP_WINDOW_HOURS`, 48h
 * — `recapWindowMs` mirrors it), ordered most-recently-ended first. The reliable ended-recap feed
 * (the rail's `pov=upcoming` drops ended events); window + attendance scoping
 * are server-side, and consumers still keep only the `ended-recap` phase
 * client-side. Auth-only (401s for guests) — consumers gate with `enabled`.
 */
export const liveRecapQueryOptions = () =>
  queryOptions({
    queryKey: eventsLiveRecapKey(),
    queryFn: async () => {
      const response = await getMyRecaps({ page: 1, per_page: LIVE_RECAP_PER_PAGE });
      return response.data.data;
    },
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
  });
